import Images from 'constants/images';
import i18n from 'constants/translation/i18n';
import { BarChartOutlined, FileExcelOutlined } from '@ant-design/icons';
import { IReportType } from 'store/ducks/Global/types';

export const reportTypes: IReportType[] = [
  {
    titleKey: 'BUSINESS_RULES_AUDIT_FINDINGS',
    title: 'Business Rules & Audit Findings',
    description:
      'Comprehensive report including business rules, gaps, audit findings, and solutions',
    filename: 'business_rules_gaps_audit_findings_solutions',
    icon: <FileExcelOutlined />,
    type: 'danger'
  },
  {
    titleKey: 'BRU_EVIDENCES',
    title: 'BRU Evidences',
    description: 'Business rules with evidence mapping and documentation',
    filename: 'bru_evidences',
    icon: <FileExcelOutlined />,
    type: 'info'
  },
  {
    titleKey: 'BRU_GAPS_AUDITS_SOLUTIONS',
    title: 'BRU Gaps Audits Solutions',
    description: 'Gaps, audit findings and solutions for business rules',
    filename: 'bru_gaps_audits_solutions',
    icon: <FileExcelOutlined />,
    type: 'success'
  },
  {
    titleKey: 'SOLUTIONS_TASKS',
    title: 'Solutions & Tasks',
    description: 'Solutions with associated tasks and workflow tracking',
    filename: 'solutions_tasks',
    icon: <FileExcelOutlined />,
    type: 'processing'
  },
  {
    titleKey: 'GAPS_FILTER',
    title: 'Gaps Filter',
    description: 'Filtered gaps based on business rules',
    filename: 'gaps_filter',
    icon: <FileExcelOutlined />,
    type: 'warning'
  },
  {
    titleKey: 'AUDIT_FINDINGS_FILTER',
    title: 'Audit Findings Filter',
    description: 'Filtered audit findings based on business rules',
    filename: 'audit_findings_filter',
    icon: <FileExcelOutlined />,
    type: 'error'
  }
];

export const reportRiskAndControlsTypes: IReportType[] = [
  {
    titleKey: 'CONTROL_REPORT',
    title: 'Control report',
    description:
      'Complete overview of tasks, identified gaps, and their solutions',
    filename: 'control_report',
    icon: <BarChartOutlined />,
    type: 'primary'
  },
  {
    titleKey: 'PROCESSES_AND_CRITICALITIES',
    title: 'Processes and Criticalities',
    description:
      'Overview of business processes with associated risks and controls',
    detailedDescription: {
      title: 'Process Risk and Control Overview',
      content:
        'This worksheet provides an overview of business processes and the risks and controls associated with them. The structure allows the relationship between operational processes, identified risks, and implemented controls to be analysed in a consolidated view.',
      fields: [
        {
          name: 'business_process_l4_title',
          description:
            'The Level-4 business process representing the most granular operational activity.'
        },
        {
          name: 'process_type_name',
          description:
            'The classification of the process according to the defined process taxonomy.'
        },
        {
          name: 'process_criticality_name',
          description:
            'The overall criticality assessment of the process. This assessment reflects the combined risk exposure and the effectiveness of the controls allocated to the respective process. It therefore represents a consolidated evaluation derived from the risks associated with the process and the mitigating controls implemented.'
        },
        {
          name: 'risk_title',
          description: 'The identified risk linked to the respective process.'
        },
        {
          name: 'control_name',
          description:
            'The control implemented to mitigate the associated risk.'
        }
      ],
      footer:
        'Based on the process criticality, the information in this report can be aggregated across organisational levels (e.g., group, division, region, legal entity, or business unit). This enables consolidated views of process risk exposure and supports risk-based prioritisation and planning of internal audit activities. Overall, this report provides a transparent linkage between processes, risks, and controls, supporting governance oversight, risk analysis, and audit planning.'
    },
    filename: 'process_overview',
    icon: <BarChartOutlined />,
    type: 'primary'
  },
  {
    titleKey: 'RISK_INVENTORY',
    title: 'Risk Inventory',
    description: 'Comprehensive inventory of risks with associated processes',
    detailedDescription: {
      title: 'Workbook Description - Root Cause and Risk Overview',
      content:
        'This worksheet provides an overview of identified risks and their associated root causes. The report enables the classification of risks according to their underlying causes and thematic categories, supporting structured analysis and prioritisation of remediation actions.',
      fields: [
        {
          name: 'root_cause',
          description:
            'The underlying cause or driver that may lead to the occurrence of a risk. This field allows grouping and filtering of risks by their origin.'
        },
        {
          name: 'risk_title',
          description: 'The name or short title describing the identified risk.'
        },
        {
          name: 'description',
          description:
            'A detailed explanation of the risk, including the circumstances under which it may occur.'
        },
        {
          name: 'category',
          description:
            'The classification of the risk within a predefined risk category or taxonomy.'
        }
      ],
      footer:
        'This view supports root-cause-oriented risk analysis, helping organisations identify systemic weaknesses and address risks at their source rather than only treating symptoms.'
    },
    filename: 'risk_inventory',
    icon: <BarChartOutlined />,
    type: 'primary'
  },
  {
    titleKey: 'PROCESS_INVENTORY',
    title: 'Process Inventory',
    description: 'Hierarchical view of business processes from L1 to L4',
    filename: 'process_inventory',
    icon: <BarChartOutlined />,
    type: 'primary'
  },
  {
    titleKey: 'GROUP_ORGANIZATION',
    title: 'Group Organization',
    description: 'Organizational hierarchy from Group to Business Unit',
    detailedDescription: {
      title: 'Workbook Description - Organisational Structure',
      content:
        'This worksheet provides the organisational structure of the company, allowing business activities, risks, and controls to be mapped to the relevant organisational units. The structure is represented across several organisational levels forming a hierarchical relationship. Each lower level is mapped to the respective higher organisational level.',
      fields: [
        {
          name: 'group_name',
          description:
            'The highest organisational level, representing the overall corporate group.'
        },
        {
          name: 'division_name',
          description: 'Major organisational division within the group.'
        },
        {
          name: 'region_name',
          description: 'Geographic region within the division.'
        },
        {
          name: 'legal_entity_name',
          description: 'The specific legal entity operating within the region.'
        },
        {
          name: 'business_unit_name',
          description:
            'The operational business unit within the legal entity responsible for specific activities or processes.'
        }
      ],
      footer:
        'This hierarchical structure supports clear allocation of responsibilities, governance transparency, and structured reporting across the organisation.'
    },
    filename: 'group_organization',
    icon: <BarChartOutlined />,
    type: 'primary'
  },
  {
    titleKey: 'RISKS_CONTROLS_GAPS',
    title: 'Risks Controls Gaps',
    description: 'Unlinked risks and controls analysis',
    filename: 'risks_controls_gaps',
    icon: <BarChartOutlined />,
    type: 'primary'
  }
];

export const getReportTypes = (t: (key: string) => string): IReportType[] =>
  reportTypes.map((r) => ({
    ...r,
    title: r.titleKey ? t(r.titleKey) : r.title
  }));

export const getReportRiskAndControlsTypes = (
  t: (key: string) => string
): IReportType[] =>
  reportRiskAndControlsTypes.map((r) => ({
    ...r,
    title: r.titleKey ? t(r.titleKey) : r.title
  }));

const workingAreas = (t?: any) => {
  const areas = [
    {
      area: t ? t('LIBRARY') : i18n.t('LIBRARY'),
      infoText: t ? t('LIBRARY_TEXT') : i18n.t('LIBRARY_TEXT'),
      imgSrc: Images.library,
      href: '/library'
    },
    {
      area: t ? t('WORKFLOWS') : i18n.t('WORKFLOWS'),
      infoText: t ? t('COMP_EST_TEXT') : i18n.t('COMP_EST_TEXT'),
      imgSrc: Images.complianceEstabCardIcon,
      href: '/workingAreaDetails/rules-to-evidence'
    },
    {
      area: t ? t('CONTROL_RISKS') : i18n.t('CONTROL_RISKS'),
      infoText: t ? t('CONTROL_RISKS_TEXT') : i18n.t('CONTROL_RISKS_TEXT'),
      imgSrc: Images.internalControls,
      href: '/internal_controls'
    },
    {
      area: t ? t('USER_ACCESS') : i18n.t('USER_ACCESS'),
      infoText: t ? t('USER_ACCESS_TEXT') : i18n.t('USER_ACCESS_TEXT'),
      imgSrc: Images.internalControls,
      href: '/user_access'
    },
    {
      area: t ? t('WHO_AM_I') : i18n.t('WHO_AM_I'),
      infoText: t ? t('WHO_AM_I_TEXT') : i18n.t('WHO_AM_I_TEXT'),
      imgSrc: Images.internalControls,
      href: '/who_am_i'
    }
  ];
  return areas;
};

export default workingAreas;
